import { useState } from 'react';
import { deleteOrgFile, readOrgFile, writeOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { scanReferencesToEntity } from '../../utils/intelligence';
import { SEVERITY_LEVELS, INCIDENT_STATUSES } from '../../utils/enums';
import Badge from '../shared/Badge';
import { BG_CONTENT, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, BTN_STATUS_FORWARD, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, STATUS_INFO, BG_SHELL, STATUS_BAD, STATUS_WARN, SURFACE_WHITE, FONT_BODY, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_LIST_ITEM } from '../../theme';
import ConfirmDialog from '../shared/ConfirmDialog';
import { dateStatus, incidentStaleness } from '../../utils/dateStatus';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const SEVERITIES = SEVERITY_LEVELS;
const STATUSES   = INCIDENT_STATUSES;

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    newIncident:           '+ Nuevo incidente',
    loading:               'Cargando incidentes…',
    emptyTitle:            'No hay incidentes registrados.',
    emptyHint:             'Crea el primer incidente con el botón "+ Nuevo incidente".',
    colRef:                'Referencia',
    colTitle:              'Título',
    colSeverity:           'Severidad',
    colDetected:           'Detectado',
    colStatus:             'Estado',
    colActions:            'Acciones',
    filterAll:             'Todos',
    filterTitle:           'Título',
    filterTitlePh:         'Buscar por título…',
    filterSeverity:        'Severidad',
    filterStatus:          'Estado',
    viewBtn:               'Ver',
    editBtn:               'Editar',
    markContained:         'Contener',
    markResolved:          'Resolver',
    closeBtn:              'Cerrar',
    reopenBtn:             'Reabrir',
    deleteBtn:             'Eliminar',
    deleteConfirm:         '¿Eliminar el incidente "{name}"? Esta acción no se puede deshacer.',
    selectAll:             'Seleccionar todo',
    nSelected:             '{n} seleccionados',
    deleteSelected:        'Eliminar seleccionados',
    deleteSelectedConfirm: '¿Eliminar los siguientes incidentes?\n\n{names}\n\nEsta acción no se puede deshacer.',
    saveError:             'Error al guardar. Inténtalo de nuevo.',
    saving:                'Guardando…',
    severityLabels: {
      'Critical': 'Crítico',
      'High':     'Alto',
      'Medium':   'Moderado',
      'Low':      'Bajo',
    },
    statusLabels: {
      'Open':          'Abierto',
      'Investigating': 'Investigando',
      'Contained':     'Contenido',
      'Resolved':      'Resuelto',
      'Closed':        'Cerrado',
    },
    viewTitle:             'Ver incidente',
    backToList:            '← Volver al listado',
    vRef:                  'Referencia',
    vTitle:                'Título',
    vServiceDeskRef:       'Ref. service desk',
    vDescription:          'Descripción',
    vDetectedAt:           'Detectado',
    vReportedBy:           'Reportado por',
    vCoordinator:          'Coordinador',
    vCategory:             'Categoría',
    vSeverity:             'Severidad',
    vStatus:               'Estado',
    vClosedAt:             'Fecha de cierre',
    vAffectedAssets:       'Activos afectados',
    vAffectedServices:     'Servicios afectados',
    vContainment:          'Acciones de contención',
    vEradication:          'Acciones de erradicación',
    vRecovery:             'Acciones de recuperación',
    vRootCause:            'Causa raíz',
    vLessonsLearned:       'Lecciones aprendidas',
    vControlImprovements:  'Mejoras de controles',
    vGdprNotifiable:       'Notificable RGPD',
    vNis2Notifiable:       'Notificable NIS2',
    vNotificationDetails:  'Detalles de notificación',
    vControls:             'Controles del Anexo A',
    vEvidence:             'Evidencias vinculadas',
    vLinkedNonconformities: 'No conformidades vinculadas',
    vTimeline:             'Cronología',
    vCommunications:       'Comunicaciones',
    vYes:                  'Sí',
    vNo:                   'No',
    vEmpty:                '—',
    badgeOverdue:          'Vencido',
    badgeSoon:             'Vence pronto',
    badgeStale:            'Estancado',
    formCancel:            'Cancelar',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    newIncident:           '+ New incident',
    loading:               'Loading incidents…',
    emptyTitle:            'No incidents recorded yet.',
    emptyHint:             'Create the first incident using the "+ New incident" button.',
    colRef:                'Reference',
    colTitle:              'Title',
    colSeverity:           'Severity',
    colDetected:           'Detected',
    colStatus:             'Status',
    colActions:            'Actions',
    filterAll:             'All',
    filterTitle:           'Title',
    filterTitlePh:         'Search by title…',
    filterSeverity:        'Severity',
    filterStatus:          'Status',
    viewBtn:               'View',
    editBtn:               'Edit',
    markContained:         'Contain',
    markResolved:          'Resolve',
    closeBtn:              'Close',
    reopenBtn:             'Reopen',
    deleteBtn:             'Delete',
    deleteConfirm:         'Delete incident "{name}"? This cannot be undone.',
    selectAll:             'Select all',
    nSelected:             '{n} selected',
    deleteSelected:        'Delete selected',
    deleteSelectedConfirm: 'Delete the following incidents?\n\n{names}\n\nThis cannot be undone.',
    saveError:             'Could not save. Please try again.',
    saving:                'Saving…',
    severityLabels: {
      'Critical': 'Critical',
      'High':     'High',
      'Medium':   'Medium',
      'Low':      'Low',
    },
    statusLabels: {
      'Open':          'Open',
      'Investigating': 'Investigating',
      'Contained':     'Contained',
      'Resolved':      'Resolved',
      'Closed':        'Closed',
    },
    viewTitle:             'View incident',
    backToList:            '← Back to list',
    vRef:                  'Reference',
    vTitle:                'Title',
    vServiceDeskRef:       'Service desk ref.',
    vDescription:          'Description',
    vDetectedAt:           'Detected',
    vReportedBy:           'Reported by',
    vCoordinator:          'Coordinator',
    vCategory:             'Category',
    vSeverity:             'Severity',
    vStatus:               'Status',
    vClosedAt:             'Closed at',
    vAffectedAssets:       'Affected assets',
    vAffectedServices:     'Affected services',
    vContainment:          'Containment actions',
    vEradication:          'Eradication actions',
    vRecovery:             'Recovery actions',
    vRootCause:            'Root cause',
    vLessonsLearned:       'Lessons learned',
    vControlImprovements:  'Control improvements',
    vGdprNotifiable:       'GDPR notifiable',
    vNis2Notifiable:       'NIS2 notifiable',
    vNotificationDetails:  'Notification details',
    vControls:             'Annex A controls',
    vEvidence:             'Linked evidence',
    vLinkedNonconformities: 'Linked nonconformities',
    vTimeline:             'Timeline',
    vCommunications:       'Communications log',
    vYes:                  'Yes',
    vNo:                   'No',
    vEmpty:                '—',
    badgeOverdue:          'Overdue',
    badgeSoon:             'Due soon',
    badgeStale:            'Stale',
    formCancel:            'Cancel',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── COLOR MAPS ────────────────────────────────────────────────────────────────
const SEVERITY_COLORS = {
  'Critical': { bg: ERROR_BG, text: ERROR_TEXT },
  'High':     { bg: '#ffedd5', text: '#c2410c' },
  'Medium':   { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Low':      { bg: BG_MUTED, text: TEXT_MUTED },
};

const STATUS_COLORS = {
  'Open':          { bg: ERROR_BG, text: ERROR_TEXT },
  'Investigating': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Contained':     { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Resolved':      { bg: STATUS_INFO.bg, text: '#1d4ed8' },
  'Closed':        { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

function formatDatetime(dt) {
  if (!dt) return '—';
  return dt.replace('T', ' ').slice(0, 16);
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function IncidentList({ incidents, loading, org, lang, onNew, onEdit, onView, onNavigate, onIncidentsChanged }) {
  const t = T[lang] || T.en;

  const [filterText,     setFilterText]     = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [selectedIds,    setSelectedIds]    = useState(new Set());
  const [listError,      setListError]      = useState('');
  const [savingId,       setSavingId]       = useState(null);
  const [confirmState,   setConfirmState]   = useState(null);
  const [scanningDelete, setScanningDelete] = useState(false);

  const showError = (msg) => { setListError(msg); setTimeout(() => setListError(''), 4000); };

  const safeIncidents = Array.isArray(incidents) ? incidents : [];
  const filtered = safeIncidents.filter((inc) => {
    if (filterSeverity && inc.severity !== filterSeverity) return false;
    if (filterStatus   && inc.status   !== filterStatus)   return false;
    if (filterText) {
      const q = filterText.trim().toLowerCase();
      if (q && !(inc.title || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const selectedInView = filtered.filter((inc) => selectedIds.has(inc.id));
  const allChecked     = filtered.length > 0 && selectedInView.length === filtered.length;
  const someChecked    = selectedInView.length > 0;

  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(filtered.map((inc) => inc.id)));
    else         setSelectedIds(new Set());
  };
  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (checked) next.add(id); else next.delete(id); return next; });
  };

  const handleStatusChange = async (incident, newStatus) => {
    const id      = incident.id;
    const now     = new Date().toISOString();
    const updates = { status: newStatus, updatedAt: now };
    if (newStatus === 'Closed') updates.closedAt = now;
    if (newStatus === 'Open')   updates.closedAt = null;
    const updated = { ...incident, ...updates };
    updated.changeLog = appendChangeLog(incident, updated, org.consultantPersonId || 'system');
    setSavingId(id);
    let succeeded = false;
    try {
      await writeOrgFile(org.id, `incidents/${id}.json`, updated);
      onIncidentsChanged();
      succeeded = true;
      setSavingId(id + '-ok');
      setTimeout(() => setSavingId(prev => prev === id + '-ok' ? null : prev), 1500);
    } catch {
      showError(t.saveError);
    } finally {
      if (!succeeded) setSavingId(null);
    }
  };

  // §11 rule 42 — Incident deactivation: status="Closed".
  const handleDeactivateIncident = async (incident) => {
    try {
      const existing = await readOrgFile(org.id, `incidents/${incident.id}.json`).catch(() => incident);
      const patch = { status: 'Closed' };
      const merged = { ...existing, ...patch, updatedAt: new Date().toISOString() };
      merged.changeLog = appendChangeLog(existing, patch, org.consultantPersonId || 'system');
      await writeOrgFile(org.id, `incidents/${incident.id}.json`, merged);
      onIncidentsChanged();
    } catch { showError(t.saveError); }
  };

  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const handleDeleteOne = async (incident) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'incident', incident.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm.replace('{name}', incident.title),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `incidents/${incident.id}.json`);
            setSelectedIds((prev) => { const next = new Set(prev); next.delete(incident.id); return next; });
            onIncidentsChanged();
          } catch { showError(t.saveError); }
        },
      });
      return;
    }
    const showDeactivate = incident.status !== 'Closed';
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
      secondaryAction: showDeactivate ? {
        label: t.deactivateInstead,
        color: BTN_DESTRUCTIVE_OUTLINE,
        onClick: () => { setConfirmState(null); handleDeactivateIncident(incident); },
      } : undefined,
    });
  };

  const handleDeleteSelected = async () => {
    if (!selectedInView.length) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    const items = [...selectedInView];
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(inc => scanReferencesToEntity(org.id, 'incident', inc.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((inc, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(inc);
      else blocked.push({ record: inc, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohort) => {
      await Promise.all(cohort.map((inc) => deleteOrgFile(org.id, `incidents/${inc.id}.json`).catch(() => {})));
      setSelectedIds(new Set());
      onIncidentsChanged();
    };
    if (blocked.length === 0) {
      const names = items.map((inc) => inc.title).join(', ');
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{names}', names),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(items);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.title || record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable);
      },
    });
  };

  function getStatusAction(status) {
    if (status === 'Open')      return { label: t.markContained, newStatus: 'Contained' };
    if (status === 'Contained') return { label: t.markResolved,  newStatus: 'Resolved' };
    if (status === 'Resolved')  return { label: t.closeBtn,      newStatus: 'Closed' };
    // §11 rule 34 — Closed incidents are immutable. Reopen moved to
    // Settings per rule 35 ("Reopen closed record"). No per-record UI.
    return null;
  }

  // ── Styles ──────────────────────────────────────────────────────────────────
  const thSt = {
    ...FONT_TINY, color: TEXT_DISABLED,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    padding: '0.4rem 0.75rem', borderBottom: `1px solid ${BORDER_DEFAULT}`,
    fontWeight: 600, textAlign: 'left', background: BG_CONTENT, whiteSpace: 'nowrap',
  };
  const tdSt = {
    ...FONT_LIST_ITEM,
    padding: '0.55rem 0.75rem', borderBottom: `1px solid ${BG_SHELL}`,
    verticalAlign: 'middle',
  };
  const filterSelectSt = {
    ...FONT_SMALL,
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5,
    padding: '0.3rem 0.5rem', background: SURFACE_WHITE, color: TEXT_DEFAULT, cursor: 'pointer',
  };
  const btnSmall = {
    ...BTN_SECONDARY,
    ...FONT_TINY,
    borderRadius: 5,
    padding: '0.2rem 0.5rem', cursor: 'pointer',
  };

  return (
    <div>
      {listError && (
        <div style={{ ...FONT_LABEL, marginBottom: '0.75rem', padding: '0.6rem 1rem', background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8, color: ERROR_TEXT }}>
          {listError}
        </div>
      )}
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterTitle}</span>
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={t.filterTitlePh}
              style={{ ...filterSelectSt, cursor: 'text', minWidth: 220 }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterSeverity}</span>
            <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {SEVERITIES.map((v) => <option key={v} value={v}>{t.severityLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {someChecked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
            {t.nSelected.replace('{n}', selectedInView.length)}
          </span>
          <button onClick={handleDeleteSelected} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>{t.deleteSelected}</button>
        </div>
      )}

      {/* Loading */}
      {loading && <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>{t.loading}</p>}

      {/* Empty state */}
      {!loading && safeIncidents.length === 0 && (
        <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '2.5rem', textAlign: 'center' }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>{t.emptyTitle}</p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>{t.emptyHint}</p>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thSt, width: 36, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                  <input type="checkbox" title={t.selectAll} checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)} />
                </th>
                <th style={thSt}>{t.colRef}</th>
                <th style={{ ...thSt, minWidth: 180 }}>{t.colTitle}</th>
                <th style={thSt}>{t.colSeverity}</th>
                <th style={{ ...thSt, minWidth: 140 }}>{t.colDetected}</th>
                <th style={thSt}>{t.colStatus}</th>
                <th style={thSt}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inc, idx) => {
                const action = getStatusAction(inc.status);
                return (
                  <tr key={inc.id} style={{ background: idx % 2 === 0 ? BG_SURFACE : 'transparent' }}>
                    <td style={{ ...tdSt, textAlign: 'center', padding: '0.55rem 0.5rem' }}>
                      <input type="checkbox" checked={selectedIds.has(inc.id)} onChange={(e) => toggleOne(inc.id, e.target.checked)} />
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {inc.ref || inc.id}
                    </td>
                    <td style={{ ...tdSt, fontWeight: 600, color: TEXT_HEADING }}>{inc.title}</td>
                    <td style={tdSt}><Badge label={inc.severity} displayLabel={t.severityLabels[inc.severity] || inc.severity} colorMap={SEVERITY_COLORS} /></td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {formatDatetime(inc.detectedAt)}
                    </td>
                    <td style={tdSt}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                        <Badge label={inc.status} displayLabel={t.statusLabels[inc.status] || inc.status} colorMap={STATUS_COLORS} />
                        {(() => {
                          const ds = dateStatus(inc.lessonsLearnedReviewDate);
                          if (!ds) return null;
                          return (
                            <span style={{
                              ...FONT_TINY,
                              fontWeight: 600,
                              background: ds === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                              color:      ds === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                              borderRadius: 4,
                              padding: '0.15rem 0.45rem',
                              marginLeft: '0.35rem',
                              whiteSpace: 'nowrap',
                            }}>
                              {ds === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                            </span>
                          );
                        })()}
                        {(() => {
                          const stale = incidentStaleness(inc);
                          if (!stale) return null;
                          return (
                            <span style={{
                              ...FONT_TINY,
                              fontWeight: 600,
                              background: STATUS_BAD.bg,
                              color:      STATUS_BAD.text,
                              borderRadius: 4,
                              padding: '0.15rem 0.45rem',
                              marginLeft: '0.35rem',
                              whiteSpace: 'nowrap',
                            }}>
                              {t.badgeStale}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => onView(inc)} style={btnSmall}>{t.viewBtn}</button>
                        <button onClick={() => onEdit(inc)} style={btnSmall}>{t.editBtn}</button>
                        {action && savingId === inc.id + '-ok' && (
                          <span style={{ fontSize: '0.78rem', color: BRAND_PRIMARY_DARK, fontWeight: 600, padding: '0.2rem 0.4rem' }}>✓</span>
                        )}
                        {action && savingId !== inc.id + '-ok' && (
                          <button
                            onClick={() => handleStatusChange(inc, action.newStatus)}
                            disabled={savingId === inc.id}
                            style={{
                              ...btnSmall,
                              border: BTN_STATUS_FORWARD.border,
                              color: BTN_STATUS_FORWARD.color,
                              opacity: savingId === inc.id ? 0.6 : 1,
                              cursor:  savingId === inc.id ? 'default' : 'pointer',
                            }}
                          >{savingId === inc.id ? t.saving : action.label}</button>
                        )}
                        <button onClick={() => handleDeleteOne(inc)} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>{t.deleteBtn}</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
